import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { authService, fileService } from '../../services/api';
import FileUpload from './FileUpload';
import ProcessingStatus from './ProcessingStatus';
import UsageStats from './UsageStats';
// import ConsentFlow from '../consent/ConsentFlow'; // TEMPORARILY DISABLED
// import { useConsent } from '../../hooks/useConsent'; // TEMPORARILY DISABLED
// import { useSnackbar } from 'notistack'; // Removed unused import

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { lastMessage, isConnected } = useWebSocket();
  // const { enqueueSnackbar } = useSnackbar(); // Removed unused import
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  
  // Main branch deployment trigger - version 19 (restore OAuth code handling)

  // Fetch user usage stats
  const { data: usageStats, refetch: refetchUsage, error: usageError, isLoading: usageLoading } = useQuery(
    'userUsage',
    authService.getUserUsage,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: 3, // Retry failed requests 3 times
      retryDelay: 1000, // Wait 1 second between retries
    }
  );

  // Fetch processing status
  const { data: processingStatus, refetch: refetchStatus } = useQuery(
    'processingStatus',
    fileService.getProcessingStatus,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Clear any old status when component mounts (fresh login)
  useEffect(() => {
    setCurrentStatus(null);
    setFileUploaded(false);
  }, []);

  // Update current status when processing status changes
  useEffect(() => {
    if (processingStatus) {
      console.log('[DASHBOARD] Processing status received:', processingStatus);
      setCurrentStatus(processingStatus);
      
      // If status is completed, refetch usage stats to update the count
      if (processingStatus.status === 'completed') {
        console.log('[DASHBOARD] Status is completed, refetching usage stats');
        refetchUsage();
      }
      
      // Reset to file upload if status is ready
      if (processingStatus.status === 'ready') {
        console.log('[DASHBOARD] Status is ready, resetting to file upload');
        setFileUploaded(false);
        setCurrentStatus(null);
      }
    }
  }, [processingStatus, refetchUsage]);

  // Update status from WebSocket messages
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'status_update') {
      const statusData = {
        status: lastMessage.status,
        filename: lastMessage.filename,
        message: lastMessage.message,
        timestamp: lastMessage.timestamp,
      };
      
      setCurrentStatus(statusData);
      
      // Refetch usage stats when processing completes
      if (lastMessage.status === 'completed') {
        refetchUsage();
      }
    }
  }, [lastMessage, refetchUsage]);

  const handleFileUploaded = (filename?: string) => {
    // Mark that a file has been uploaded
    setFileUploaded(true);
    
    // Immediately show "uploaded" status and clear any previous completed status
    setCurrentStatus({
      status: 'uploaded',
      filename: filename || 'Medical Report',
      message: 'File uploaded successfully, queued for processing',
      timestamp: new Date().toISOString()
    });
    
    // Refresh status after file upload to get real-time updates
    refetchStatus();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2 }}>
          Welcome back, {user?.given_name ? user.given_name.substring(0, 3) + '***' : user?.name ? user.name.substring(0, 3) + '***' : 'User'}! 👋
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
          Upload your medical reports and get AI-powered analysis delivered to your email.
        </Typography>
        
        {/* Connection Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Chip
            label={isConnected ? 'Real-time updates enabled' : 'Connecting...'}
            color={isConnected ? 'success' : 'default'}
            size="small"
          />
          {user?.email && (
            <Chip
              label={`Signed in as ${user.email}`}
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Usage Stats Card */}
        <Grid item xs={12} md={4}>
          <UsageStats usageStats={usageStats} isLoading={usageLoading} error={usageError} />
        </Grid>

        {/* Processing Status Card - Show only when actively processing a file */}
        {fileUploaded && (
          <Grid item xs={12} md={8}>
            <ProcessingStatus 
              status={currentStatus} 
              onRetry={() => refetchStatus()}
              usageStats={usageStats}
            />
          </Grid>
        )}

        {/* File Upload Section - Show when not actively processing a file */}
        {!fileUploaded && (
          <Grid item xs={12}>
            {/* TEMPORARY: Always show file upload (consent disabled) */}
            {usageStats?.limit_reached ? (
              <Card>
                <CardContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Free Usage Limit Reached
                    </Typography>
                    <Typography variant="body2">
                      You have used all <strong>{usageStats.limit} free report analysis attempts</strong>.
                      To continue using our service, please contact us at{' '}
                      <strong>info@2ndopinionai.org</strong> for upgrade options.
                    </Typography>
                  </Alert>
                  
                  <Box
                    sx={{
                      background: '#f5f5f5',
                      padding: 3,
                      borderRadius: 2,
                      textAlign: 'center',
                      border: '2px dashed #ccc',
                    }}
                  >
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      📤 File Upload (Disabled)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Upload feature is disabled after {usageStats.limit} free uses
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <FileUpload 
                onFileUploaded={handleFileUploaded} 
                usageStats={usageStats}
              />
            )}
          </Grid>
        )}


        {/* How it Works Section - Above Why Choose */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" sx={{ mb: 3, textAlign: 'center' }}>
                ℹ️ How it Works
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                      1. Upload
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Select your medical report PDF file
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                      2. Submit
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Click submit to securely upload your report
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                      3. Process
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Our AI analyzes your report for abnormalities
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2E86AB', mb: 1 }}>
                      4. Receive
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Get detailed analysis via email within minutes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Features Section - Why Choose 2nd Opinion - Below How it Works */}
        <Grid item xs={12}>
          <Typography variant="h4" component="h2" sx={{ mb: 3, textAlign: 'center' }}>
            🌟 Why Choose 2nd Opinion?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h5" component="h3" sx={{ color: '#2E86AB', mb: 2 }}>
                    🔬 Advanced Analysis
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    AI-powered medical report analysis with detailed insights and recommendations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h5" component="h3" sx={{ color: '#2E86AB', mb: 2 }}>
                    📧 Email Delivery
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Receive comprehensive analysis directly in your email inbox
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h5" component="h3" sx={{ color: '#2E86AB', mb: 2 }}>
                    🔒 Secure & Private
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Your medical data is encrypted and processed securely in the cloud
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;


